import { CalendarSearch, CircleCheck, CircleX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  type LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { Button, Card, CardContent, CardFooter } from '@virtual-care/shadcn';

import { ProfileCard } from '../../components/header';
import Header from '../../components/header/header';
import { standardReservationStore } from '../../hooks/reservation/standard.ts';

export function bookingRequestStatusLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  if (
    url.searchParams.get('ok') === '1' &&
    standardReservationStore.read() !== null
  ) {
    standardReservationStore.write(null);
  }
  return { retryPath: '/book-appointment/payments' };
}

export default function RequestStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData() as { retryPath?: string } | null;
  const retryPath = loaderData?.retryPath ?? '/';
  const paymentStatus = searchParams.get('ok');

  const isPaymentSuccess = paymentStatus === '1';
  const isPaymentFailure = paymentStatus === '0';

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('booking.appointmentTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>

      <div className="flex h-[460px] items-center justify-center">
        <div className="card-container">
          <Card className="card">
            <CardContent className="flex flex-col gap-5">
              <div className="flex flex-col items-center justify-center gap-4">
                {isPaymentFailure && (
                  <>
                    <CircleX
                      className="h-24 w-24 text-red-500"
                      strokeWidth={2}
                    />
                    <h2 className="text-center text-3xl font-bold text-gray-900">
                      {t('booking.requestStatus.paymentFailed')}
                    </h2>
                    <p className="max-w-md text-center text-base text-gray-500">
                      {t('booking.requestStatus.paymentFailedDescription')}
                    </p>
                  </>
                )}

                {isPaymentSuccess && (
                  <>
                    <CircleCheck
                      className="text-primary h-24 w-24"
                      strokeWidth={2}
                    />
                    <h2 className="text-center text-3xl font-bold text-gray-900">
                      {t('booking.requestStatus.underReview')}
                    </h2>
                    <p className="max-w-md text-center text-base text-gray-500">
                      {t('booking.requestStatus.underReviewDescription')}
                    </p>
                  </>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex w-full justify-center gap-4">
              {isPaymentFailure ? (
                <>
                  <Button
                    variant={'outline'}
                    onClick={() => navigate(retryPath)}
                    className="secondary-button"
                  >
                    {t('booking.requestStatus.retry')}
                  </Button>
                  <Button
                    variant={'default'}
                    onClick={() => navigate('/')}
                    className="primary-button"
                  >
                    {t('booking.requestStatus.home')}
                  </Button>
                </>
              ) : (
                <Button
                  variant={'default'}
                  onClick={() => navigate('/')}
                  className="primary-button"
                >
                  {t('booking.requestStatus.home')}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
}
